<template>
  <NuxtLink
      v-if="data.status === 'published'"
      :data-domain="data.domain?.color?.name"
      class="program-card"
      :class="{'program-card--in-slider': props.inSlider}"
      :href="(data.external_url) ? data.external_url : `/program/${joinSlugId(props.data)}/`"
      :target="(data.external_url) ? '_blank' : '_self'"
  >
    <AppImageFocal v-if="data.image" :data="data.image" :renditions="renditions" class="program-card__image" aria-hidden="true"/>
    <div class="program-card__content"
         :class="{
            'program-card__content--televisie-online': programType === 'televisie' || programType === 'online',
            'program-card__content--podcast-radio': programType === 'podcast' || programType === 'radio',
    }">
      <span
          class="program-card__label"
          :class="{
        'program-card__label--podcast-radio': programType === 'podcast' || programType === 'radio',
        'program-card__label--not-podcast-radio': programType !== 'podcast' && programType !== 'radio',
      }">
        <AppIconLoader v-if="programType === 'podcast' || programType === 'radio'" :name="programType"
                       variant="small-medium" aria-hidden="true"/>
        <small class="program-card__small">{{ data.type.title }}</small>
      </span>
      <AppHeadingResizing
          v-if="programType === 'televisie' || programType === 'online'"
          :key="headingKey"
          tag="h3"
          :label="data.title"
          :animate="false"
          :indented="false"
          class="program-card__heading-resizing"
      />
      <h3 v-else class="program-card__title" v-html="strippedTitle"></h3>
      <AppIconLoader v-if="data.external_url" name="external" aria-hidden="true" variant="small-medium"
                     class="program-card__icon"/>
    </div>
  </NuxtLink>
</template>

<script setup lang="ts">
import {computed, onMounted, onUnmounted, ref} from 'vue'
import {TImageRendition} from '~/types/other-types'
import {throttle} from '~/utils/throttle-debounce'
import {TProgramPage} from "~/types/page-types";
import {TProgramCard} from "~/types/card-types";
import {joinSlugId} from "#imports";

const renditions: TImageRendition[] = [
  {width: 369, height: 235, breakpoint: 'xs'},
  {width: 550, height: 350, breakpoint: 'sm'},
  {width: 369, height: 235, breakpoint: 'md'},
]

const props = defineProps<{
  data: TProgramCard | TProgramPage,
  inSlider?: Boolean | false,
}>()

const programType = computed(() => {
  return props.data.type.title.toLowerCase()
})

const strippedTitle = computed(() => {
  let title = props.data.title.replace('{{', '')
  return title.replace('}}', '')
})

const headingKey = ref(0)

function rerenderHeading() {
  throttle(headingKey.value += 1, 1000)
}

onMounted(() => {
  window.addEventListener('resize', rerenderHeading)
})

onUnmounted(() => {
  window.removeEventListener('resize', rerenderHeading)
})
</script>

<style scoped lang="postcss">
.program-card {
  @mixin app-box-shadow-with-hover;
  border-width: 3px;
  border-color: var(--color-blue-aqua);
  background-color: var(--color-blue-aqua);
  position: relative;
  display: grid;
  width: 100%;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  overflow: clip;
  border-radius: 0.25rem;
  aspect-ratio: 369 / 235;

  &__image {
    grid-column-start: 1;
    grid-row-start: 1;
    display: flex;
  }

  &__content {
    position: relative;
    z-index: 10;
    grid-column-start: 1;
    grid-row-start: 1;
    display: flex;
    width: 100%;
    flex-direction: column;
    justify-content: space-between;
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0.35) 0%, rgba(0, 0, 0, 0) 20%, rgba(0, 0, 0, 0) 40%, rgba(0, 0, 0, 0.35) 100%);

    &--televisie-online {
      align-items: center;
      padding-left: 2.5rem;
      padding-right: 2.5rem;
    }

    &--podcast-radio {
      padding-left: 1.5rem;
      padding-right: 1.5rem;
    }
  }

  &__label {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;

    &--podcast-radio {
      align-self: flex-start;
    }

    &--not-podcast-radio {
      margin-top: 0.5rem;
    }
  }

  &__small {
    font-family: Midnight, sans-serif;
    font-size: 0.75rem;
    text-transform: uppercase;
    line-height: 0.8;
  }

  &__heading-resizing {
    width: 100%;
    font-family: Midnight, sans-serif;
    font-size: 1.125rem;
    text-transform: uppercase;
    line-height: 0.8;
    text-shadow: 0px 1px 4px rgba(0,0,0,0.25);
  }

  &__title {
    hyphens: auto;
    font-family: Midnight, sans-serif;
    font-size: 25px;
    line-height: 0.8;
  }

  &__icon {
    position: absolute;
    right: 0.75rem;
    top: 0.75rem;
  }

  &--in-slider {
    height: 235px;
    width: 369px;
  }

  &[data-domain="yellow"] {
    border-color: var(--color-yellow);
    background-color: var(--color-yellow);
    color: var(--color-yellow);
  }

  &[data-domain="violet"] {
    border-color: var(--color-violet);
    background-color: var(--color-violet);
    color: var(--color-violet);
  }

  &[data-domain="red"] {
    border-color: var(--color-red);
    background-color: var(--color-red);
    color: var(--color-red);
  }
}
</style>
